<script setup lang="ts">
import type { AwDropdownMenuItem } from "@asap/design";
import type { TaskSubjectEntity } from "@asap/shared/schemas/task/context";
import TaskGroupList from "~/components/task/TaskGroupList.vue";
import { toOpenTaskGroupId, useTaskGroups } from "~/components/task/composables/useTaskGroups";
import { useTaskTrigger } from "~/components/task/composables/useTaskTrigger";
import { TASK_SCOPES, type TaskScope } from "~/components/task/task-scope";

const props = defineProps<{
  entity: TaskSubjectEntity;
  entityId: string;
}>();

const { t } = useI18n();
const { open: openTaskCreateModal, lastCreatedTask } = useTaskTrigger();

const selectedScope = ref<TaskScope>(TASK_SCOPES[0].value);
const selectedScopeOption = computed(
  () => TASK_SCOPES.find((option) => option.value === selectedScope.value) ?? TASK_SCOPES[0]
);

const scopeMenuItems = computed<AwDropdownMenuItem[]>(() =>
  TASK_SCOPES.map((option) => ({
    label: t(option.labelKey),
    onClick: () => {
      selectedScope.value = option.value;
    },
  }))
);

const { taskAccordionGroups, groupList, isTaskDisplayedAsDone, loadGroup, load, onGroupOpen, onTaskDoneChange } =
  useTaskGroups({
    scope: selectedScope,
    getSubject: () => ({ entity: props.entity, entityId: props.entityId }),
  });

watch(selectedScope, load);

watch(lastCreatedTask, async (task) => {
  const isTaskOnThisSubject = task?.context.subjects?.some(
    (subject) => subject.entity === props.entity && subject.entity_id === props.entityId
  );
  if (!task || !isTaskOnThisSubject) {
    return;
  }

  const group = taskAccordionGroups[toOpenTaskGroupId(task.due_date)];
  await loadGroup(group.id);

  if (group.items.some((item) => item.id === task.id)) {
    group.isOpen = true;
  }
});

onMounted(load);

// The create modal reads the subject from the current route
const openCreateModal = () => openTaskCreateModal(null);

const ui = {
  root: "flex min-h-0 flex-1 flex-col gap-4",
  header: "flex shrink-0 items-center justify-between gap-4",
};
</script>

<template>
  <div :class="ui.root">
    <div :class="ui.header">
      <AwDropdownMenu :content="{ side: 'bottom', align: 'start' }" :items="scopeMenuItems">
        <AwButton variant="outline" color="secondary" trailing-icon="i-ph-caret-down">
          {{ $t(selectedScopeOption.labelKey) }}
        </AwButton>
      </AwDropdownMenu>
      <AwButton leading-icon="i-ph-plus" @click="openCreateModal">{{ $t("tasks.create.createTask") }}</AwButton>
    </div>

    <TaskGroupList
      :groups="groupList"
      :is-task-displayed-as-done="isTaskDisplayedAsDone"
      @on-group-open="onGroupOpen"
      @on-task-done-change="onTaskDoneChange"
    />
  </div>
</template>
